"use client";

import { useState } from "react";
import Image from "next/image";
import type { VideoThumbnailsBlock, Media } from "@/payload-types";
import { cn } from "@/lib/utils";
import { RichText } from "@/components/RichText";
import { getImageData } from "@/lib/media-utils";

const gridLineColors: Record<string, string> = {
  brand: "border-brand/25",
  amber: "border-amber-500/25",
  white: "border-white/15",
  gray: "border-gray-700/30",
};

function getEmbedUrl(url: string): string | null {
  if (!url) return null;

  const youtubeRegex =
    /(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/;
  const youtubeMatch = url.match(youtubeRegex);
  if (youtubeMatch?.[1]) {
    return `https://www.youtube-nocookie.com/embed/${youtubeMatch[1]}`;
  }

  const vimeoRegex = /(?:vimeo\.com\/|player\.vimeo\.com\/video\/)(\d+)/;
  const vimeoMatch = url.match(vimeoRegex);
  if (vimeoMatch?.[1]) {
    return `https://player.vimeo.com/video/${vimeoMatch[1]}`;
  }

  return null;
}

export function VideoThumbnailsClient({
  heading,
  description,
  videos,
  gridLineColor = "brand",
  hashLink,
}: VideoThumbnailsBlock) {
  const [selectedIndex, setSelectedIndex] = useState(0);

  if (!videos || videos.length === 0) return null;

  const lineColor = gridLineColors[gridLineColor ?? "brand"] ?? gridLineColors.brand;
  const selected = videos[selectedIndex];

  // Resolve video source — either an embed URL or an uploaded file URL
  const isUpload = selected?.source === "upload";
  const embedUrl = !isUpload && selected?.videoUrl ? getEmbedUrl(selected.videoUrl) : null;
  const uploadUrl = isUpload && selected?.videoFile && typeof selected.videoFile === "object" && "url" in selected.videoFile
    ? (selected.videoFile as Media).url
    : null;

  return (
    <section
      id={hashLink ?? undefined}
      className={cn("relative w-full bg-black text-white border-x border-t border-b", lineColor)}
    >
      {/* Main 3-col × 3-row grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 lg:grid-rows-3">
        {/* Row 1 — Heading + Thumbnails */}
        {/* The heading sits in the top portion, thumbnails across the bottom */}
        <div className={cn("lg:col-span-3 lg:row-span-1 flex flex-col")}>
          {/* Heading area */}
          <div className="flex flex-col items-center justify-center px-6 pt-12 pb-6 lg:pt-16 lg:pb-8">
            {heading && (
              <h2 className="text-3xl md:text-4xl font-subheading text-center">
                {heading}
              </h2>
            )}
            {description && (
              <RichText
                content={description}
                className="rich-text-sm rich-text-dark text-center mt-3 max-w-2xl"
              />
            )}
          </div>

          {/* Thumbnails grid */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 px-6 pb-6 lg:px-8 lg:pb-8">
            {videos.map((video, i) => {
              const { url: thumbUrl, alt: thumbAlt } = getImageData(
                video.thumbnail as Media,
                video.title
              );
              return (
                <button
                  key={video.id || i}
                  onClick={() => setSelectedIndex(i)}
                  className={cn(
                    "group text-left transition-opacity",
                    selectedIndex === i ? "opacity-100" : "opacity-60 hover:opacity-80"
                  )}
                >
                  <div
                    className={cn(
                      "relative aspect-video overflow-hidden rounded border-2 transition-colors",
                      selectedIndex === i
                        ? "border-white"
                        : "border-transparent"
                    )}
                  >
                    {thumbUrl && (
                      <Image
                        src={thumbUrl}
                        alt={thumbAlt}
                        fill
                        className="object-cover"
                        sizes="(max-width: 640px) 50vw, (max-width: 1024px) 33vw, 16vw"
                      />
                    )}
                  </div>
                  <p className="mt-2 text-sm font-semibold truncate">
                    {video.title}
                  </p>
                  {video.shortDescription && (
                    <p className="text-xs text-gray-400 line-clamp-2 mt-0.5">
                      {video.shortDescription}
                    </p>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Rows 2-3 — Selected video info (left) + Video player (right, 2×2) */}
        {/* Info cell — top left of rows 2-3 */}
        <div className="lg:col-span-1 lg:row-span-1 flex flex-col justify-center p-6 lg:p-8">
          {selected?.title && (
            <h3 className="text-xl md:text-2xl font-subheading">
              {selected.title}
            </h3>
          )}
          {selected?.fullDescription && (
            <RichText
              content={selected.fullDescription}
              className="rich-text-sm rich-text-dark mt-3"
            />
          )}
        </div>

        {/* Video player — spans 2 cols × 2 rows */}
        <div className="lg:col-span-2 lg:row-span-2 p-3 lg:p-0">
          <div className="relative w-full h-full min-h-[300px] lg:min-h-0">
            {embedUrl && (
              <iframe
                key={embedUrl}
                src={embedUrl}
                title={selected?.title || "Video"}
                className="absolute inset-0 w-full h-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            )}
            {uploadUrl && (
              <video
                key={uploadUrl}
                controls
                className="absolute inset-0 w-full h-full object-contain bg-black"
              >
                <source src={uploadUrl} type={(selected?.videoFile && typeof selected.videoFile === "object" && "mimeType" in selected.videoFile ? (selected.videoFile as Media).mimeType : "video/mp4") ?? "video/mp4"} />
              </video>
            )}
          </div>
        </div>

        {/* Empty cell — bottom left */}
        <div className="hidden lg:block lg:col-span-1 lg:row-span-1" />
      </div>

      {/* Decorative grid lines */}
      <div
        className="absolute inset-0 pointer-events-none z-20 hidden lg:block"
        aria-hidden="true"
      >
        {/* Vertical column dividers */}
        <div className="absolute inset-0 flex">
          <div className={cn("w-1/3 border-r", lineColor)} />
          <div className={cn("w-1/3 border-r", lineColor)} />
          <div className="w-1/3" />
        </div>

        {/* Horizontal row dividers */}
        <div
          className={cn("absolute left-0 w-full border-t", lineColor)}
          style={{ top: "33.333%" }}
        />
        <div
          className={cn("absolute right-0 w-2/3 border-t", lineColor)}
          style={{ top: "66.667%" }}
        />
      </div>
    </section>
  );
}
